/*
 * Pure display and validation helpers for the Sales Team roster.
 */

import { WEEKDAYS } from './api';
import type { MemberStatus, RepEditDraft, TeamLeave, TeamLevel, TeamMember, Weekday } from './api';
import { format_range } from './leaves';

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

const WEEKDAY_LABELS: Record<Weekday, string> = {
  MON: 'Mon',
  TUE: 'Tue',
  WED: 'Wed',
  THU: 'Thu',
  FRI: 'Fri',
  SAT: 'Sat',
  SUN: 'Sun'
};

export const weekday_label = (d: Weekday): string => WEEKDAY_LABELS[d] || d;

// shift_end earlier than shift_start means the shift crosses midnight.
export const is_overnight = (start: string | null, end: string | null): boolean =>
  !!start && !!end && end < start;

export const format_shift = (
  start: string | null | undefined,
  end: string | null | undefined
): string => {
  if (!start || !end) return '—';
  const base = `${start} – ${end} IST`;
  return is_overnight(start, end) ? `${base} (overnight)` : base;
};

export const format_weekly_off = (days: Weekday[] | null | undefined): string => {
  const set = new Set(days || []);
  const ordered = WEEKDAYS.filter((d) => set.has(d));
  return ordered.length ? ordered.map(weekday_label).join(', ') : 'None';
};

export const format_next_leave = (leave: TeamLeave | null | undefined): string => {
  if (!leave) return '—';
  const range = format_range(leave.from_date, leave.to_date);
  return leave.note ? `${range} · ${leave.note}` : range;
};

const LEVEL_RANK: Record<TeamLevel, number> = { MANAGER: 0, L3: 1, L2: 2, L1: 3 };
const UNSET_RANK = 4;

const level_rank = (level: TeamLevel | null | undefined): number =>
  level && level in LEVEL_RANK ? LEVEL_RANK[level] : UNSET_RANK;

// Active before Left, then MANAGER/L3/L2/L1/unset, then name.
export const sort_roster = (members: TeamMember[] | null | undefined): TeamMember[] =>
  [...(members || [])].sort((a, b) => {
    const sa = a.status === 'LEFT' ? 1 : 0;
    const sb = b.status === 'LEFT' ? 1 : 0;
    if (sa !== sb) return sa - sb;
    const la = level_rank(a.level);
    const lb = level_rank(b.level);
    if (la !== lb) return la - lb;
    return (a.name || '').localeCompare(b.name || '');
  });

export type BadgeTone = 'success' | 'warning' | 'muted';

export interface StatusBadge {
  key: string;
  label: string;
  tone: BadgeTone;
}

export const status_badges = (
  member: Pick<TeamMember, 'status' | 'on_leave_today' | 'configured'>
): StatusBadge[] => {
  if (member.status === 'LEFT') return [{ key: 'left', label: 'Left', tone: 'muted' }];
  const out: StatusBadge[] = [{ key: 'active', label: 'Active', tone: 'success' }];
  if (member.on_leave_today) out.push({ key: 'leave', label: 'On leave', tone: 'warning' });
  if (!member.configured) out.push({ key: 'unset', label: 'Not configured', tone: 'muted' });
  return out;
};

export const LEVEL_OPTIONS: Array<{ value: TeamLevel; label: string }> = [
  { value: 'L1', label: 'L1 — lead to booked' },
  { value: 'L2', label: 'L2 — booked to joined' },
  { value: 'L3', label: 'L3 — joined to paid' },
  { value: 'MANAGER', label: 'Manager' }
];

export const STATUS_OPTIONS: Array<{ value: MemberStatus; label: string }> = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'LEFT', label: 'Left' }
];

// Returns the first problem, or null when the edit can be saved.
export const validate_rep_edit = (
  draft: Partial<RepEditDraft> | null | undefined
): string | null => {
  if (!draft) return 'Nothing to save';
  if (!draft.level || !LEVEL_OPTIONS.some((o) => o.value === draft.level)) return 'Pick a level';
  if (!draft.status || !STATUS_OPTIONS.some((o) => o.value === draft.status))
    return 'Pick a status';
  const start = draft.shift_start || '';
  const end = draft.shift_end || '';
  if (!!start !== !!end) return 'Set both shift start and end, or neither';
  if (start && (!TIME_RE.test(start) || !TIME_RE.test(end))) return 'Shift times must be HH:mm';
  if (start && start === end) return 'Shift start and end cannot be the same';
  const off = draft.weekly_off || [];
  if (off.some((d) => !WEEKDAYS.includes(d))) return 'Unknown weekly-off day';
  if (new Set(off).size >= WEEKDAYS.length) return 'A rep cannot be off every day';
  return null;
};
